import {
    CommandBar,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    ICommandBarItemProps, IDropdownOption,
    Label,
    mergeStyleSets,
    MessageBar,
    MessageBarType,
    PanelType, Pivot, PivotItem,
    PrimaryButton,
} from "@fluentui/react";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {toast} from "react-toastify";
import {
    useDeleteSavedSearchMutation,
    useGetSavedSearchQuery,
    useSaveSearchMutation
} from "../../store/endpoints/SavedSearchEndpoints";

import {
    useGetStorefrontsQuery,
} from "../../store/endpoints/StorefrontSettingsEndpoints";

import {
    useGetOrganizationFeatureSettingsQuery,
    useGetOrganizationQuery
} from "../../store/endpoints/OrganizationsEndpoints"

import {
    ISavedSearch, ISavedSearchRequest,
    ISavedSearchStorefrontDetails,
    ISearchRequest
} from "../../models/Search";
import EditableImage from "../DetailsModal/EditableImage";
import SavedSearchStorefrontSettings from "./SavedSearchStorefrontSettings";
import AddSavedSearchToBundleButton from "./AddSavedSearchToBundleButton";
import {useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";
import {cloneDeep, debounce} from "lodash";
import DataField from "../Common/DataField";
import ToggleField from "../Common/ToggleField";
import PanelWrapper from "../Common/PanelWrapper";
import {useMsal} from "@azure/msal-react";
import {SavedSearchExportSubExtensionOverrideForm} from "../ExtensionInstallation/ExtensionDataForm";
import SubExtensionDropdown from "../ExtensionFolios/SubExtensionDropdown"; 
import {ExtensionType} from "../../Constants";
import {useGetVendorsQuery} from "../../store/Api";
import {useGetWebhooksQuery} from "../../store/endpoints/WebhooksEndpoints";
import {WEBHOOK_TYPE_DPL_EXPORT} from "../../models/Webhooks";

interface ISaveSearchPanel {
    criteria?: ISearchRequest
    savedSearchId?: string;
    closer: () => void;
    onUpdate?: (newId?: string) => void;
    onDelete?: () => void;
}

const AUTOSAVE_DELAY_MS = 1000;

const SaveSearchPanel: React.FC<ISaveSearchPanel> = props => {
    const storefronts = useGetStorefrontsQuery();
    const [defaultCatalog, setDefaultCatalog] = useState<string>();
    const [saveSearch] = useSaveSearchMutation();
    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const organization = useGetOrganizationQuery(selectedOrganization ?? '', {skip: !selectedOrganization || selectedOrganization === ''});
    const [deleteSavedSearch] = useDeleteSavedSearchMutation();
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const currentUsername = useMsal().accounts[0]?.username;

    const fetchedSavedSearch = useGetSavedSearchQuery(
        {id: props.savedSearchId ?? ''},
        {skip: !props.savedSearchId}
    );

    const orgFeatures = useGetOrganizationFeatureSettingsQuery(selectedOrganization ?? '', {skip: !selectedOrganization || selectedOrganization === ''});
    const [saving, setSaving] = useState(false);
    const [savedSearch, setSavedSearch] = useState<ISavedSearch>({});
    const currentIdRef = useRef<string | undefined>(props.savedSearchId);
    const isInitialized = useRef(false);
    const initializedForId = useRef<string | undefined>(undefined);
    const isOwner = !savedSearch.username || savedSearch.username.toLowerCase() === currentUsername?.toLowerCase();
    const [saveError, setSaveError] = useState<string>();
    const selectedCatalog = useSelector((state: RootState) => state.search.selectedCatalog)
    const [storefrontMap, setStorefrontMap] = useState<{[p: string]: ISavedSearchStorefrontDetails}>();
    const [storefrontIds, setStorefrontIds] = useState<string[]>([]);
    const vendors = useGetVendorsQuery(defaultCatalog ?? "", {skip: !defaultCatalog})
    const webhooks = useGetWebhooksQuery({pageSize: 500});

    useEffect(() => {
        if(organization.data) {
            setDefaultCatalog(organization.data.defaultCatalog);
        }
    },[organization.data]);
    
    useEffect(() => {
        if(storefronts.data?.items){
            const ids = storefronts.data.items.map(storefront => storefront.id ?? '')
            if(ids) {
                setStorefrontIds(ids)
            }
        }
    }, [storefronts.data]);

    useEffect(() =>{
        const propsSavedSearch = fetchedSavedSearch.data;

        // If we're expecting server data, wait until it arrives before initializing.
        if (props.savedSearchId && !propsSavedSearch) return;

        // Only re-initialize when the target ID changes, not when the cache
        // is updated after an auto-save (which would revert local edits).
        const incomingId = propsSavedSearch?.id ?? 'new';
        if (initializedForId.current === incomingId) return;
        initializedForId.current = incomingId;

        isInitialized.current = false;
        if(props.criteria) {
            const criteria: ISavedSearchRequest = cloneDeep(props.criteria)
            criteria.catalog = selectedCatalog?.name
            setSavedSearch({
                id: propsSavedSearch?.id,
                criteria: criteria,
                name: propsSavedSearch?.name,
                storefronts: propsSavedSearch?.storefronts,
                includeInAllStorefronts: propsSavedSearch?.includeInAllStorefronts,
                shared: propsSavedSearch?.shared,
                username: propsSavedSearch?.username,
                image: propsSavedSearch?.image,
                imageId: propsSavedSearch?.imageId,
                markup: propsSavedSearch?.markup,
                markupType: propsSavedSearch?.markupType,
                minimumCost: propsSavedSearch?.minimumCost,
                minimumPrice: propsSavedSearch?.minimumPrice,
                storefrontDetails: propsSavedSearch?.storefrontDetails,
                overrideDescription: propsSavedSearch?.overrideDescription,
                dynamicProductListing: propsSavedSearch?.dynamicProductListing,
                exportVendors: propsSavedSearch?.exportVendors,
                exportExtensionSettings: propsSavedSearch?.exportExtensionSettings,
                exportProductExtension: propsSavedSearch?.exportProductExtension,
                exportWebhook: propsSavedSearch?.exportWebhook,
            })
        } else if (propsSavedSearch) {
            setSavedSearch(propsSavedSearch)
        }
        // Mark as initialized on next tick so the setState above doesn't trigger auto-save
        setTimeout(() => { isInitialized.current = true; }, 0);
    }, [props.criteria, fetchedSavedSearch.data])

    useEffect(() => {
        if(storefrontIds && savedSearch) {
            const detailsMap: { [p: string]: ISavedSearchStorefrontDetails } = {};
            storefrontIds.forEach(id => {
                detailsMap[id] = {storefrontId: id};
            });
            savedSearch?.storefrontDetails?.forEach(storefrontDetail => {
                if (storefrontDetail.storefrontId && storefrontDetail.storefrontId) {
                    detailsMap[storefrontDetail.storefrontId] = storefrontDetail
                }
            })
            setStorefrontMap(detailsMap)
        }
    }, [savedSearch, storefrontIds]);
    
    const debouncedSave = useCallback(
        debounce((search: ISavedSearch) => {
            if (!search.name?.trim()) return;
            setSaving(true);
            setSaveError(undefined);
            const objectName = search.name.trim();
            const toastId = toast.loading(`Saving ${objectName}…`);
            const payload = currentIdRef.current ? {...search, id: currentIdRef.current} : {...search, id: undefined};
            saveSearch({savedSearch: payload}).unwrap().then(result => {
                setSaving(false);
                if (!result.success) {
                    setSaveError(result.message ?? "Error saving search");
                    toast.update(toastId, { type: 'error', render: `Failed to save ${objectName}`, isLoading: false, autoClose: 4000 });
                    return;
                }
                if (result.id && !currentIdRef.current) {
                    currentIdRef.current = result.id;
                    props.onUpdate?.(result.id);
                }
                toast.update(toastId, { type: 'success', render: `${objectName} saved`, isLoading: false, autoClose: 3000 });
            }).catch(() => {
                setSaving(false);
                setSaveError("Error saving search");
                toast.update(toastId, { type: 'error', render: `Failed to save ${objectName}`, isLoading: false, autoClose: 4000 });
            });
        }, AUTOSAVE_DELAY_MS),
        [saveSearch]
    );

    // Trigger auto-save whenever savedSearch changes (but not on initial data load)
    useEffect(() => {
        if (!isInitialized.current) return;
        debouncedSave(savedSearch);
        return () => { debouncedSave.cancel(); };
    }, [savedSearch, debouncedSave]);

    const contentStyles = mergeStyleSets({
        root: { zIndex: '10 !important', marginTop: 'var(--app-header-offset)' },
        main: { marginTop: 'var(--app-header-offset)' },
        scrollableContent: { backgroundColor: 'rgb(255, 255, 255)' },
        navigation: { backgroundColor: 'rgb(255, 255, 255)' },
        footerInner: { backgroundColor: 'rgb(255, 255, 255)' },
        content: { backgroundColor: 'rgb(255, 255, 255)' },
        commands: { backgroundColor: 'rgb(255, 255, 255)' },
    });

    const handleDelete = () => {
        if (!savedSearch.id) return;
        setDeleting(true);
        deleteSavedSearch(savedSearch.id).unwrap().then(() => {
            setDeleting(false);
            setConfirmDeleteOpen(false);
            props.onDelete?.();
            props.closer();
        }).catch(() => {
            setDeleting(false);
            setSaveError('Error deleting saved search');
        });
    };

    const saveAsCopy = () => {
        if (!savedSearch.name?.trim()) return;
        debouncedSave.cancel();
        setSaving(true);
        setSaveError(undefined);
        saveSearch({savedSearch: {...savedSearch, id: undefined}}).unwrap().then(result => {
            setSaving(false);
            if (!result.success) {
                setSaveError(result.message ?? "Error saving search");
                return;
            }
            props.onUpdate?.(result.id);
            props.closer();
        }).catch(() => {
            setSaving(false);
            setSaveError("Error saving search");
        });
    };

    const headerCommands: ICommandBarItemProps[] = [
        ...(isOwner && savedSearch.id ? [{
            key: 'delete',
            text: 'Delete',
            iconProps: { iconName: 'Delete', style: { color: '#d13438' } },
            buttonStyles: { root: { color: '#d13438' }, label: { color: '#d13438' } },
            disabled: deleting,
            onClick: () => setConfirmDeleteOpen(true),
        }] : []),
        {
            key: 'saveAsCopy',
            text: 'Save as Copy',
            iconProps: { iconName: 'SaveAs' },
            disabled: !savedSearch.name?.trim() || saving,
            onClick: saveAsCopy,
        },
    ];

    const renderHeader = () => (
        <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '24px' }}>
                <h1 style={{ margin: 0, flexGrow: 1 }}>Saved Search</h1>
            </div>
            {isOwner && <CommandBar items={headerCommands} styles={{ root: { paddingLeft: 12, borderBottom: '1px solid #edebe9' } }} />}
            {saveError && <MessageBar messageBarType={MessageBarType.error}>{saveError}</MessageBar>}
        </div>
    );

    const updateStorefrontDetails = (details: {[p: string]: ISavedSearchStorefrontDetails}) => {
        const keys = Object.keys(details)
        const detailsArray = keys.map(key => details[key])
        setSavedSearch({...savedSearch, storefrontDetails: detailsArray})
    }

    const canShowOverrideFields = () => {
        if(!savedSearch) return false;
        if(!savedSearch.shared) return false;
        return !(orgFeatures?.data?.enableDynamicProductListings && savedSearch.dynamicProductListing);
    }

    const onChangeVendors = (event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption): void => {
        if (item) {
            setSavedSearch({...savedSearch,
                exportVendors: item.selected ? [...savedSearch.exportVendors ?? [], item.key as string] : savedSearch.exportVendors?.filter(key => key !== item.key) });
        }
    };

    return (
        <div>
            <PanelWrapper
                isOpen={true}
                onDismiss={() => {
                    debouncedSave.flush();
                    props.closer();
                }}
                onRenderHeader={renderHeader}
                className={"saved-search-panel"}
                type={PanelType.custom}
                customWidthPx={1200}
                isFooterAtBottom={true}
                footerButtons={[
                    <AddSavedSearchToBundleButton savedSearchId={savedSearch.id} />,
                    <DefaultButton text='Close' onClick={() => { debouncedSave.flush(); props.closer(); }} />
                ]}
                title={"Saved Search"}
                styles={{
                    root: { zIndex: 10, marginTop: 'var(--app-header-offset)' },
                    content: contentStyles.content,
                    commands: contentStyles.commands,
                    scrollableContent: contentStyles.scrollableContent,
                    navigation: contentStyles.navigation,
                    footerInner: contentStyles.footerInner,
                }}
            >
                <div className={'panel-grouping'}>
                    <DataField
                        label={'Saved Search Name'}
                        value={savedSearch.name}
                        onChange={(e, v) => setSavedSearch({...savedSearch, name: v})}
                        toolTip={"This will also be the product name for any storefronts"}
                    />
                    {!savedSearch.name?.trim() && (
                        <MessageBar messageBarType={MessageBarType.info}>
                            Enter a name to enable auto-save.
                        </MessageBar>
                    )}

                    <div style={{paddingTop: '1em'}}>
                        <ToggleField
                            inline={true}
                            checked={savedSearch.shared}
                            onChange={(e, v) => setSavedSearch({...savedSearch, shared: v})}
                            label={'Shared'}
                            toolTip={"If checked, the saved search result will be available to other users, and can be used as a storefront item."}
                        />
                        {savedSearch.shared && orgFeatures?.data?.enableDynamicProductListings &&
                            <div style={{paddingTop: '1em'}}>
                                <ToggleField inline={true}
                                    checked={savedSearch.dynamicProductListing}
                                    onChange={(e, v) => setSavedSearch({...savedSearch, dynamicProductListing: v})}
                                    label={'Dynamic Product Listing'}
                                    toolTip={"If checked, the entire results set from this saved search will appear on the storefront if updated within the last 24 hours."}
                                />
                            </div>
                        }
                    </div>

                    {canShowOverrideFields() &&
                        <div style={{paddingTop: '1em'}}>
                            <DataField
                                multiline={true}
                                label={'Override Item Description'}
                                value={savedSearch.overrideDescription}
                                onChange={(e, v) => setSavedSearch({...savedSearch, overrideDescription: v})}
                                toolTip={"Override the item description. If blank, the saved search result item's description will be used."}
                            />
                            <Label style={{paddingTop: '2em'}}>Storefront Feature Thumbnail</Label>
                            <EditableImage
                                image={{
                                    url: savedSearch.image ?? '',
                                    type: 'Custom',
                                    name: 'Storefront Feature Thumbnail',
                                    id: savedSearch.imageId ?? crypto.randomUUID(),
                                    isCustom: true,
                                }}
                                editMode={true}
                                onChange={(id, url) => setSavedSearch({...savedSearch, image: url, imageId: id})}
                                onClear={() => {setSavedSearch({...savedSearch, image: '', imageId: undefined})}}
                            />
                        </div>
                    }
                </div>
                {savedSearch.shared &&
                    <Pivot style={{minWidth: '500px'}}>
                        <PivotItem headerText={'Storefront'} >
                            <div className={'panel-pivot-grouping'}>
                                <ToggleField
                                    inline={true}
                                    checked={savedSearch.includeInAllStorefronts ?? false}
                                    onChange={(e, v) => setSavedSearch({...savedSearch, includeInAllStorefronts: v})}
                                    label={'Include All Storefronts'}
                                    toolTip={"If checked, this saved search will be included in all storefronts. If unchecked, you can select which storefronts to include."}
                                />
                                <SavedSearchStorefrontSettings
                                    savedSearchId={savedSearch.id ?? ""}
                                    includeAllStorefronts={savedSearch.includeInAllStorefronts ?? false}
                                    minimumCost={savedSearch.minimumCost}
                                    minimumPrice={savedSearch.minimumPrice}
                                    onStorefrontMinimumCostChange={(minimumCost) => {
                                        setSavedSearch({...savedSearch, minimumCost: minimumCost})
                                    }}
                                    onStorefrontMinimumPriceChange={(minimumPrice) => {
                                        setSavedSearch({...savedSearch, minimumPrice: minimumPrice})
                                    }}
                                    storefrontOverrideMarkup={savedSearch.markup}
                                    storefrontOverrideMarkupType={savedSearch.markupType}
                                    includedStorefronts={savedSearch.storefronts}
                                    storefronts={storefrontMap}
                                    onIncludedStorefrontsChange={(includedStorefronts) => {
                                        setSavedSearch({...savedSearch, storefronts: includedStorefronts})
                                    }}
                                    onStorefrontsChange={(storefronts) => updateStorefrontDetails(storefronts)}
                                    onStorefrontOverrideMarkupChange={(markup) => {
                                        setSavedSearch({...savedSearch, markup: markup})
                                    }}
                                    onStorefrontOverrideMarkupTypeChange={(markupType) => {
                                        setSavedSearch({...savedSearch, markupType: markupType})
                                    }}
                                />
                            </div>
                        </PivotItem>
                        {savedSearch.dynamicProductListing && 
                        <PivotItem headerText={'Export'}>
                            <div className={'panel-pivot-grouping'}>
                                <SubExtensionDropdown
                                    type={ExtensionType.SavedSearchBulkExport}
                                    onChange={(extensionId) => setSavedSearch({...savedSearch, exportProductExtension: extensionId, exportWebhook: undefined})}
                                    extension={savedSearch.exportProductExtension}
                                    label={"Export Product Extension"}
                                    includeWebhook={true}
                                />
                                {savedSearch.exportProductExtension === 'WEBHOOK' && (
                                    <Dropdown
                                        label={"Export Webhook"}
                                        placeholder={"Select a webhook..."}
                                        selectedKey={savedSearch.exportWebhook ?? ''}
                                        options={[
                                            {key: '', text: 'None'},
                                            ...(webhooks.data?.items ?? [])
                                                .filter(w => w.type === WEBHOOK_TYPE_DPL_EXPORT)
                                                .map(w => ({key: w.id ?? '', text: w.name ?? w.id ?? ''}))
                                        ]}
                                        onChange={(_, option) => setSavedSearch({...savedSearch, exportWebhook: option?.key as string || undefined})}
                                        disabled={webhooks.isLoading || webhooks.isFetching}
                                    />
                                )}
                                {savedSearch.exportProductExtension && savedSearch.exportProductExtension !== 'WEBHOOK' &&
                                <Dropdown
                                    placeholder={"Select Vendors"}
                                    multiSelect={true}
                                    label={"Vendor Filter"}
                                    selectedKeys={savedSearch.exportVendors}
                                    options={vendors?.data?.items?.map(vendor => ({ key: vendor, text: vendor })) ?? []}
                                    onChange={onChangeVendors}
                                    disabled={vendors.isLoading || vendors.isFetching}
                                />}
                                {savedSearch.exportProductExtension && savedSearch.exportProductExtension !== 'WEBHOOK' &&
                                <SavedSearchExportSubExtensionOverrideForm
                                    key={savedSearch.exportProductExtension}
                                    extensionId={savedSearch.exportProductExtension ?? ''}
                                    savedSearch={savedSearch}
                                    onChange={(details) => setSavedSearch({...savedSearch, ...details})}
                                />}
                            </div>
                        </PivotItem>}
                    </Pivot>
                }
            </PanelWrapper>

            <Dialog
                hidden={!confirmDeleteOpen}
                onDismiss={() => setConfirmDeleteOpen(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Delete Saved Search',
                    subText: `Are you sure you want to delete "${savedSearch.name}"? This cannot be undone.`,
                }}
                modalProps={{ isBlocking: true }}
            >
                <DialogFooter>
                    <PrimaryButton
                        text={deleting ? 'Deleting...' : 'Delete'}
                        onClick={handleDelete}
                        disabled={deleting}
                        styles={{ root: { backgroundColor: '#d13438', borderColor: '#d13438' } }}
                        iconProps={{ iconName: 'Delete' }}
                    />
                    <DefaultButton text='Cancel' onClick={() => setConfirmDeleteOpen(false)} disabled={deleting} />
                </DialogFooter>
            </Dialog>
        </div>
    );
}
export default SaveSearchPanel;

